import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useTheme } from "../../hooks/useTheme";
import api from "../../api/api";

const ACTIONS = ["view", "edit", "delete"];
const permIcons = { view: '👁', edit: '✏️', delete: '🗑' };
const ROLE_LABELS = { 0: 'Default', 1: 'Admin', 2: 'IT Manager', 3: 'View Only', 5: 'Support' };

// Group the flat permission list into categories -> pages -> {action: permission id}
function groupPermissions(permissions) {
    const categories = [];
    permissions.forEach(p => {
        let cat = categories.find(c => c.name === p.category);
        if (!cat) {
            cat = { name: p.category, pages: [] };
            categories.push(cat);
        }
        let page = cat.pages.find(pg => pg.name === p.name);
        if (!page) {
            page = { name: p.name, actions: {} };
            cat.pages.push(page);
        }
        page.actions[p.action] = p.id;
    });
    return categories;
}

const categoryIds = (cat) => cat.pages.flatMap(pg => Object.values(pg.actions));

function PermissionMatrixTable() {
    const { isDarkMode } = useTheme();
    const { can, refreshPermissions } = useAuth();
    const canEdit = can('Permissions', 'Permission Management', 'edit');

    const [categories, setCategories] = useState([]);
    const [roles, setRoles] = useState([]);
    const [saved, setSaved] = useState({});
    const [draft, setDraft] = useState({});
    const [selectedRole, setSelectedRole] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null);

    const fetchPermissions = useCallback(async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const res = await api.get('/permissions');
            const assigned = {};
            Object.entries(res.data.assigned || {}).forEach(([roleId, ids]) => {
                assigned[roleId] = new Set(ids);
            });
            setCategories(groupPermissions(res.data.permissions || []));
            setRoles(res.data.roles || []);
            setSaved(assigned);
            setDraft(Object.fromEntries(Object.entries(assigned).map(([k, v]) => [k, new Set(v)])));
            setSelectedRole(prev => prev ?? (res.data.roles?.[0]?.id ?? null));
        } catch (err) {
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPermissions();
    }, [fetchPermissions]);

    const roleDraft = useMemo(() => draft[selectedRole] || new Set(), [draft, selectedRole]);

    // Unsaved changes across every role: the ids whose checkbox differs from what's saved
    const pendingChanges = useMemo(() => {
        const allIds = categories.flatMap(categoryIds);
        return roles
            .map(role => {
                const d = draft[role.id] || new Set();
                const s = saved[role.id] || new Set();
                const changed = allIds.filter(id => d.has(id) !== s.has(id));
                return { admin_type_id: role.id, permission_ids: changed, granted_ids: changed.filter(id => d.has(id)) };
            })
            .filter(change => change.permission_ids.length > 0);
    }, [categories, roles, draft, saved]);

    const changeCount = pendingChanges.reduce((sum, change) => sum + change.permission_ids.length, 0);
    const rolesWithChanges = new Set(pendingChanges.map(change => change.admin_type_id));

    const setGranted = (ids, value) => {
        setSaveStatus(null);
        setDraft(prev => {
            const next = new Set(prev[selectedRole] || []);
            ids.forEach(id => (value ? next.add(id) : next.delete(id)));
            return { ...prev, [selectedRole]: next };
        });
    };

    const handleSaveAll = async () => {
        setSaving(true);
        setSaveStatus(null);
        try {
            await api.put('/permissions', { roles: pendingChanges });
            setSaved(Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, new Set(v)])));
            setSaveStatus('success');
            setTimeout(() => setSaveStatus(prev => (prev === 'success' ? null : prev)), 3000);
            refreshPermissions();
        } catch (err) {
            setSaveStatus('error');
        } finally {
            setSaving(false);
        }
    };

    const getMainCheckboxState = (cat, action) => {
        const ids = cat.pages.map(pg => pg.actions[action]).filter(Boolean);
        if (ids.length === 0) return null;
        const granted = ids.filter(id => roleDraft.has(id)).length;
        if (granted === ids.length) return "checked";
        if (granted === 0) return "unchecked";
        return "indeterminate";
    };

    const handleMainToggle = (cat, action) => {
        const ids = cat.pages.map(pg => pg.actions[action]).filter(Boolean);
        setGranted(ids, getMainCheckboxState(cat, action) !== "checked");
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[200px]">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className={`rounded-2xl border p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-white border-slate-200 text-slate-600'}`}>
                <p className="mb-3">Couldn't load permissions.</p>
                <button className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700" onClick={fetchPermissions}>
                    Try again
                </button>
            </div>
        );
    }

    const checkboxClass = "h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50";
    const naCell = <span className={isDarkMode ? 'text-slate-600' : 'text-slate-300'}>—</span>;

    return (
        <div className={`rounded-2xl border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            {/* Header bar */}
            <div className={`flex flex-wrap items-center justify-between gap-4 px-6 py-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <div className="flex items-center gap-3">
                    <span className={`text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Role:</span>
                    <div className="flex gap-1.5 flex-wrap">
                        {roles.map(role => (
                            <button
                                key={role.id}
                                onClick={() => setSelectedRole(role.id)}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                                    selectedRole === role.id
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : isDarkMode
                                            ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                            >
                                {ROLE_LABELS[role.id] || role.name}
                                {rolesWithChanges.has(role.id) && (
                                    <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-amber-400 align-middle" title="Unsaved changes"></span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Owner always has every permission.{!canEdit && ' You can view but not change permissions.'}
                    </span>
                    {saveStatus === 'success' && <span className="text-sm font-medium text-primary-600">Saved</span>}
                    {saveStatus === 'error' && <span className="text-sm font-medium text-red-500">Failed to save</span>}
                    {canEdit && (
                        <button
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                                changeCount > 0 && !saving
                                    ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm'
                                    : isDarkMode ? 'bg-slate-700 text-slate-500 cursor-not-allowed' : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                            }`}
                            disabled={changeCount === 0 || saving}
                            onClick={handleSaveAll}
                        >
                            {saving ? 'Saving...' : changeCount > 0 ? `Save All (${changeCount})` : 'Save All'}
                        </button>
                    )}
                </div>
            </div>

            {/* Matrix table */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className={isDarkMode ? 'bg-slate-700/50' : 'bg-slate-50'}>
                            <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                Permission Category
                            </th>
                            {ACTIONS.map(action => (
                                <th className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-24 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} key={action}>
                                    <span className="flex items-center justify-center gap-1.5">
                                        {permIcons[action]} {action}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700/50' : 'divide-slate-100'}`}>
                        {categories.map(cat => {
                            return (
                                <React.Fragment key={cat.name}>
                                    {/* Category header row */}
                                    <tr
                                        className={`cursor-pointer transition-colors ${isDarkMode ? 'bg-slate-800 hover:bg-slate-750' : 'bg-white hover:bg-slate-50'}`}
                                        onClick={() => setExpanded(prev => ({ ...prev, [cat.name]: !prev[cat.name] }))}
                                    >
                                        <td className="px-6 py-3">
                                            <div className="flex items-center gap-2">
                                                <svg
                                                    className={`w-4 h-4 transition-transform ${expanded[cat.name] ? 'rotate-90' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}
                                                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                                <span className={`text-sm font-bold ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                                                    {cat.name}
                                                </span>
                                                <span className={`text-xs px-1.5 py-0.5 rounded-full ${isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>
                                                    {cat.pages.length}
                                                </span>
                                            </div>
                                        </td>
                                        {ACTIONS.map(action => {
                                            const state = getMainCheckboxState(cat, action);
                                            return (
                                                <td className="px-4 py-3 text-center" key={action} onClick={e => e.stopPropagation()}>
                                                    <div className="flex justify-center">
                                                        {state === null ? naCell : (
                                                            <input
                                                                type="checkbox"
                                                                checked={state === "checked"}
                                                                disabled={!canEdit}
                                                                onChange={() => handleMainToggle(cat, action)}
                                                                className={checkboxClass}
                                                                ref={el => { if (el) el.indeterminate = state === "indeterminate"; }}
                                                            />
                                                        )}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {/* Expanded page rows */}
                                    {expanded[cat.name] && cat.pages.map(page => (
                                        <tr key={page.name} className={`transition-colors ${isDarkMode ? 'bg-slate-800/60 hover:bg-slate-700/40' : 'bg-slate-50/50 hover:bg-slate-50'}`}>
                                            <td className="py-2.5">
                                                <span className={`pl-14 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                                    {page.name}
                                                </span>
                                            </td>
                                            {ACTIONS.map(action => {
                                                const id = page.actions[action];
                                                return (
                                                    <td className="px-4 py-2.5 text-center" key={action}>
                                                        <div className="flex justify-center">
                                                            {id === undefined ? naCell : (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={roleDraft.has(id)}
                                                                    disabled={!canEdit}
                                                                    onChange={() => setGranted([id], !roleDraft.has(id))}
                                                                    className={checkboxClass}
                                                                />
                                                            )}
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default function PermissionManagement() {
    const { user } = useAuth();
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();

    useEffect(() => {
        if (!user || !user.isAdmin) {
            navigate('/');
        }
    }, [user, navigate]);

    return (
        <div className={`p-6 ${isDarkMode ? 'bg-slate-900 text-slate-100' : ''}`}>
            <h1 className={`text-3xl font-extrabold tracking-tight mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Permission Management</h1>
            <p className={`mb-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Manage what each role can view, edit, and delete</p>
            <PermissionMatrixTable />
        </div>
    );
}
